import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AFFORDABLE_DEFAULT_SUBSIDY_TYPE_ID, STUDENT } from '../../constants/occupancyType';
import { BulkSelectionState, ChartData, ChartSortOption, ColumnVisibility, FilterData, PropertyTagManagementState, Tag, UIState, WaitlistApplicationsApiResponse,WaitlistFilters, WaitlistLead, WaitlistStats } from '../../types/waitlist';

export interface PropertyData {
  id: string;
  name: string;
}

export interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
}

export interface WaitlistState {
  leads: WaitlistLead[];
  filteredLeads: WaitlistLead[];
  filters: WaitlistFilters;
  filterData: FilterData;
  activeFilters: string[];
  searchTerm: string;
  ui: UIState;
  chartData: ChartData[];
  stats: WaitlistStats;
  isLoading: boolean;
  selectedLead: WaitlistLead | null;
  properties: PropertyData[];
  occupancyTypes: Array<{ label: string; value: number }>;
  bulkSelection: BulkSelectionState;
  propertyTagManagement: PropertyTagManagementState;
  pagination: PaginationState;
  rawApplicationPages: WaitlistApplicationsApiResponse[];
  lastResetTimestamp: number; // Track when reset was last triggered
}

const initialState: WaitlistState = {
  leads: [],
  filteredLeads: [],
  filters: {},
  filterData: {
    property: '',
    propertyId: undefined,
    propertyName: undefined,
    occupancyType: 0,
    occupancyTypeName: undefined,
    leaseTerm: '',
    leaseTermId: undefined,
    leaseTermName: undefined,
    leaseStartWindowId: undefined,
    leaseStartWindowName: undefined,
    floorplanPreference: [],
    floorplanIds: [],
    floorplanNames: [],
    spaceOptions: [],
    spaceOptionIds: [],
    spaceOptionNames: [],
    // New filter fields
    application_stage_status: undefined,
    applicationStageStatusIds: [],
    applicationStageStatusNames: [],
    from_date: undefined,
    to_date: undefined,
  },
  activeFilters: [],
  searchTerm: '',
  ui: {
    isFilterPopupOpen: false,
    activeTab: 0,
    isDashboardVisible: true,
    chartSortBy: ChartSortOption.FLOOR_PLAN,
    columnVisibility: {
      name: true,
      tags: true,
      daysOnWaitlist: true,
      floorPlan: true,
      dateAdded: true,
      status: true,
      accessibility: false,
      notes: true,
      skipReason: false,
      refusals: false,
      actions: true,
    },
  },
  chartData: [],
  stats: {
    totalLeads: 0,
    urgentLeads: 0,
    conversionRate: 0,
    averageDaysOnWaitlist: 0,
  },
  isLoading: false,
  selectedLead: null,
  properties: [
    { id: '1', name: 'Campus A' },
    { id: '2', name: 'Campus B' },
    { id: '3', name: 'Campus C' },
    { id: '4', name: 'Downtown Complex' }
  ],
  occupancyTypes: [
    { label: 'Conventional', value: 1 },
    { label: 'Affordable', value: 6 },
    { label: 'Student', value: STUDENT }
  ],
  bulkSelection: {
    selectedLeadIds: [],
    isAllSelected: false,
    isIndeterminate: false,
  },
  propertyTagManagement: {
    tags: [],
    isLoading: false,
    selectedProperty: '',
    selectedOccupancy: STUDENT,
    modifiedTagIds: [],
  },
  pagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    hasMore: true,
    isLoadingMore: false,
  },
  rawApplicationPages: [],
  lastResetTimestamp: 0, // Initialize reset tracking
};

const waitlistSlice = createSlice({
  name: 'waitlist',
  initialState,
  reducers: {
    // Lead management
    setLeads: (state, action: PayloadAction<WaitlistLead[]>) => {
      return {
        ...state,
        leads: action.payload,
        filteredLeads: filterLeads(action.payload, state.searchTerm),
      };
    },
    addLeads: (state, action: PayloadAction<WaitlistLead[]>) => {
      // Append new leads to existing ones for pagination
      const nextLeads = [...state.leads, ...action.payload];
      return {
        ...state,
        leads: nextLeads,
        // Important: filter against the newly appended list, not the previous state
        filteredLeads: filterLeads(nextLeads, state.searchTerm),
      };
    },
    addLead: (state, action: PayloadAction<WaitlistLead>) => {
      return {
        ...state,
        leads: [action.payload, ...state.leads],
        filteredLeads: filterLeads(state.leads, state.searchTerm),
      };
    },
    updateLead: (state, action: PayloadAction<WaitlistLead>) => {
      const index = state.leads.findIndex(lead => lead.id === action.payload.id);
      if (index !== -1) {
        return {
          ...state,
          leads: state.leads.map((lead, i) => i === index ? action.payload : lead),
          filteredLeads: filterLeads(state.leads.map((lead, i) => i === index ? action.payload : lead), state.searchTerm),
        };
      }
      return state; // Return current state if lead not found
    },
    removeLead: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        leads: state.leads.filter(lead => lead.id !== action.payload),
        filteredLeads: filterLeads(state.leads.filter(lead => lead.id !== action.payload), state.searchTerm),
      };
    },

    // Filter management
    setFilters: (state, action: PayloadAction<WaitlistFilters>) => ({
      ...state,
      filters: action.payload,
    }),
    setFilterData: (state, action: PayloadAction<FilterData>) => ({
      ...state,
      filterData: { ...state.filterData, ...action.payload },
      activeFilters: generateFilterTags(
        { ...state.filterData, ...action.payload },
        state.occupancyTypes,
        state.properties
      ),
      pagination: {
        currentPage: 0,
        pageSize: 20,
        totalCount: 0,
        hasMore: true,
        isLoadingMore: false,
      },
      rawApplicationPages: [], // Clear raw application pages when filters change
    }),
    setLeaseTermData: (state, action: PayloadAction<{
      leaseTermId: number;
      leaseStartWindowId: number;
      leaseTermName: string;
      leaseStartWindowName: string;
    }>) => ({
      ...state,
      filterData: {
        ...state.filterData,
        leaseTermId: action.payload.leaseTermId,
        leaseStartWindowId: action.payload.leaseStartWindowId,
        leaseTermName: action.payload.leaseTermName,
        leaseStartWindowName: action.payload.leaseStartWindowName,
      },
    }),
    setActiveFilters: (state, action: PayloadAction<string[]>) => ({
      ...state,
      activeFilters: action.payload,
    }),
    removeActiveFilter: (state, action: PayloadAction<string>) => ({
      ...state,
      activeFilters: state.activeFilters.filter(filter => filter !== action.payload),
      filterData: updateFilterDataFromRemovedTag(
        state.filterData,
        action.payload,
        state.occupancyTypes,
        state.properties
      ),
    }),
    setFloorplanSelection: (state, action: PayloadAction<{ ids: number[]; names: string[] }>) => ({
      ...state,
      filterData: {
        ...state.filterData,
        floorplanIds: action.payload.ids,
        floorplanNames: action.payload.names,
        floorplanPreference: action.payload.names, // Maintain backward compatibility
      },
      activeFilters: generateFilterTags(
        {
          ...state.filterData,
          floorplanIds: action.payload.ids,
          floorplanNames: action.payload.names,
          floorplanPreference: action.payload.names,
        },
        state.occupancyTypes,
        state.properties
      ),
    }),
    setSpaceOptionsSelection: (state, action: PayloadAction<{ ids: number[]; names: string[] }>) => ({
      ...state,
      filterData: {
        ...state.filterData,
        spaceOptionIds: action.payload.ids,
        spaceOptionNames: action.payload.names,
        spaceOptions: action.payload.names, // Maintain backward compatibility
      },
      activeFilters: generateFilterTags(
        {
          ...state.filterData,
          spaceOptionIds: action.payload.ids,
          spaceOptionNames: action.payload.names,
          spaceOptions: action.payload.names,
        },
        state.occupancyTypes,
        state.properties
      ),
    }),
    clearSpaceOptionsSelection: (state) => ({
      ...state,
      filterData: {
        ...state.filterData,
        spaceOptionIds: [],
        spaceOptionNames: [],
        spaceOptions: [],
      },
      activeFilters: generateFilterTags(
        {
          ...state.filterData,
          spaceOptionIds: [],
          spaceOptionNames: [],
          spaceOptions: [],
        },
        state.occupancyTypes,
        state.properties
      ),
    }),

    // Search management
    setSearchTerm: (state, action: PayloadAction<string>) => ({
      ...state,
      searchTerm: action.payload,
      filteredLeads: filterLeads(state.leads, action.payload),
      pagination: {
        currentPage: 0,
        pageSize: 20,
        totalCount: 0,
        hasMore: true,
        isLoadingMore: false,
      },
      rawApplicationPages: [], // Clear raw application pages when search term changes
    }),

    // UI management
    setActiveTab: (state, action: PayloadAction<number>) => ({
      ...state,
      ui: { ...state.ui, activeTab: action.payload },
    }),
    setFilterPopupOpen: (state, action: PayloadAction<boolean>) => ({
      ...state,
      ui: { ...state.ui, isFilterPopupOpen: action.payload },
    }),
    toggleColumn: (state, action: PayloadAction<keyof ColumnVisibility>) => ({
      ...state,
      ui: {
        ...state.ui,
        columnVisibility: {
          ...state.ui.columnVisibility,
          [action.payload]: !state.ui.columnVisibility[action.payload],
        },
      },
    }),
    setColumnVisibility: (state, action: PayloadAction<ColumnVisibility>) => ({
      ...state,
      ui: { ...state.ui, columnVisibility: action.payload },
    }),
    setDashboardVisible: (state, action: PayloadAction<boolean>) => ({
      ...state,
      ui: { ...state.ui, isDashboardVisible: action.payload },
    }),
    toggleDashboardVisible: (state) => ({
      ...state,
      ui: { ...state.ui, isDashboardVisible: !state.ui.isDashboardVisible },
    }),
    setChartSortBy: (state, action: PayloadAction<ChartSortOption>) => ({
      ...state,
      ui: { ...state.ui, chartSortBy: action.payload },
    }),

    // Chart management
    setChartData: (state, action: PayloadAction<ChartData[]>) => ({
      ...state,
      chartData: action.payload,
    }),

    // Stats and misc
    setStats: (state, action: PayloadAction<WaitlistStats>) => ({
      ...state,
      stats: action.payload,
    }),
    setIsLoading: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isLoading: action.payload,
    }),
    setSelectedLead: (state, action: PayloadAction<WaitlistLead | null>) => ({
      ...state,
      selectedLead: action.payload,
    }),

    // Properties and occupancy types (for dynamic loading)
    setProperties: (state, action: PayloadAction<PropertyData[]>) => {
      const properties = action.payload;
      return {
        ...state,
        properties,
        activeFilters: generateFilterTags(
          state.filterData,
          state.occupancyTypes,
          properties
        ),
      };
    },
    setOccupancyTypes: (state, action: PayloadAction<Array<{ label: string; value: number }>>) => ({
      ...state,
      occupancyTypes: action.payload,
    }),

    // Bulk selection actions
    toggleSelectLead: (state, action: PayloadAction<string>) => {
      const leadId = action.payload;
      const isSelected = state.bulkSelection.selectedLeadIds.includes(leadId);

      let newSelectedLeadIds: string[];
      if (isSelected) {
        newSelectedLeadIds = state.bulkSelection.selectedLeadIds.filter(id => id !== leadId);
      } else {
        newSelectedLeadIds = [...state.bulkSelection.selectedLeadIds, leadId];
      }

      const totalLeads = state.filteredLeads.length;
      const selectedCount = newSelectedLeadIds.length;

      return {
        ...state,
        bulkSelection: {
          selectedLeadIds: newSelectedLeadIds,
          isAllSelected: selectedCount === totalLeads && totalLeads > 0,
          isIndeterminate: selectedCount > 0 && selectedCount < totalLeads,
        },
      };
    },
    selectAllLeads: (state) => ({
      ...state,
      bulkSelection: {
        selectedLeadIds: state.filteredLeads.map(lead => lead.id),
        isAllSelected: true,
        isIndeterminate: false,
      },
    }),
    deselectAllLeads: (state) => ({
      ...state,
      bulkSelection: {
        selectedLeadIds: [],
        isAllSelected: false,
        isIndeterminate: false,
      },
    }),
    clearBulkSelection: (state) => ({
      ...state,
      bulkSelection: {
        selectedLeadIds: [],
        isAllSelected: false,
        isIndeterminate: false,
      },
    }),

    // Tag management actions
    setTags: (state, action: PayloadAction<Tag[]>) => ({
      ...state,
      propertyTagManagement: {
        ...state.propertyTagManagement,
        tags: action.payload,
      },
    }),
    createTag: (state, action: PayloadAction<Omit<Tag, 'id' | 'createdAt' | 'updatedAt'>>) => {

      // Check if a tag with the same name already exists to prevent duplicates
      const existingTag = state.propertyTagManagement.tags.find(
        tag => tag.name.toLowerCase() === action.payload.name.toLowerCase()
      );

      // If tag already exists, don't add it again
      if (existingTag) {
        return state;
      }

      const newTag: Tag = {
        ...action.payload,
        id: Date.now().toString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      return {
        ...state,
        propertyTagManagement: {
          ...state.propertyTagManagement,
          tags: [...state.propertyTagManagement.tags, newTag],
        },
      };
    },
    updateTag: (state, action: PayloadAction<Tag>) => {
      const index = state.propertyTagManagement.tags.findIndex(tag => tag.id === action.payload.id);
      if (index !== -1) {
        return {
          ...state,
          propertyTagManagement: {
            ...state.propertyTagManagement,
            tags: state.propertyTagManagement.tags.map((tag, i) =>
              i === index ? { ...action.payload, updatedAt: new Date().toISOString() } : tag
            ),
          },
        };
      }
      return state;
    },
    deleteTag: (state, action: PayloadAction<string>) => ({
      ...state,
      propertyTagManagement: {
        ...state.propertyTagManagement,
        tags: state.propertyTagManagement.tags.filter(tag => tag.id !== action.payload),
      },
    }),
    toggleTagStatus: (state, action: PayloadAction<string>) => {
      const tagToToggle = state.propertyTagManagement.tags.find(tag => tag.id === action.payload);
      if (tagToToggle) {
        const updatedTag = {
          ...tagToToggle,
          isActive: !tagToToggle.isActive,
          updatedAt: new Date().toISOString(),
        };

        const newModifiedTagIds = state.propertyTagManagement.modifiedTagIds.includes(action.payload)
          ? state.propertyTagManagement.modifiedTagIds
          : [...state.propertyTagManagement.modifiedTagIds, action.payload];

        return {
          ...state,
          propertyTagManagement: {
            ...state.propertyTagManagement,
            tags: state.propertyTagManagement.tags.map(tag =>
              tag.id === action.payload ? updatedTag : tag
            ),
            modifiedTagIds: newModifiedTagIds,
          },
        };
      }
      return state;
    },
    setTagManagementProperty: (state, action: PayloadAction<string>) => ({
      ...state,
      propertyTagManagement: {
        ...state.propertyTagManagement,
        selectedProperty: action.payload,
      },
    }),
    setTagManagementOccupancy: (state, action: PayloadAction<number>) => ({
      ...state,
      propertyTagManagement: {
        ...state.propertyTagManagement,
        selectedOccupancy: action.payload,
      },
    }),
    clearModifiedTags: (state) => ({
      ...state,
      propertyTagManagement: {
        ...state.propertyTagManagement,
        modifiedTagIds: [],
      },
    }),
    addModifiedTag: (state, action: PayloadAction<string>) => {
      if (!state.propertyTagManagement.modifiedTagIds.includes(action.payload)) {
        return {
          ...state,
          propertyTagManagement: {
            ...state.propertyTagManagement,
            modifiedTagIds: [...state.propertyTagManagement.modifiedTagIds, action.payload],
          },
        };
      }
      return state;
    },
    reorderTagWithinCategory: (state, action: PayloadAction<{ tagId: string; targetTagId: string; position: 'before' | 'after' }>) => {
      const { tagId, targetTagId, position } = action.payload;
      const { tags } = state.propertyTagManagement;
      const draggedTag = tags.find(t => t.id === tagId);
      const targetTag = tags.find(t => t.id === targetTagId);
      if (!draggedTag || !targetTag || tagId === targetTagId) return state;

      const getCategory = (t: Tag) => {
        if (!t.isActive) return 'inactive';
        return (t.rank ?? 0) > 0 ? 'ranked' : 'unranked';
      };
      const draggedCategory = getCategory(draggedTag);

      const otherTags = tags.filter(t => t.id !== tagId);
      const targetIndex = otherTags.findIndex(t => t.id === targetTagId);
      const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
      const reorderedTags = [
        ...otherTags.slice(0, insertIndex),
        draggedTag,
        ...otherTags.slice(insertIndex),
      ];

      const categoryTags = reorderedTags.filter(t => getCategory(t) === draggedCategory);
      const categoryCount = categoryTags.length;

      const updatedTags = reorderedTags.map(t => {
        if (getCategory(t) !== draggedCategory) return t;
        if (draggedCategory === 'ranked') {
          const positionInCategory = categoryTags.indexOf(t);
          return { ...t, rank: categoryCount - positionInCategory, updatedAt: new Date().toISOString() };
        }
        return { ...t, updatedAt: new Date().toISOString() };
      });

      const affectedIds = updatedTags
        .filter(t => getCategory(t) === draggedCategory)
        .map(t => t.id);
      const combined = [...state.propertyTagManagement.modifiedTagIds, ...affectedIds];
      const newModifiedTagIds = combined.filter((id, idx) => combined.indexOf(id) === idx);

      return {
        ...state,
        propertyTagManagement: {
          ...state.propertyTagManagement,
          tags: updatedTags,
          modifiedTagIds: newModifiedTagIds,
        },
      };
    },

    // Pagination actions
    setCurrentPage: (state, action: PayloadAction<number>) => ({
      ...state,
      pagination: { ...state.pagination, currentPage: action.payload },
    }),
    setPageSize: (state, action: PayloadAction<number>) => ({
      ...state,
      pagination: { ...state.pagination, pageSize: action.payload },
    }),
    setTotalCount: (state, action: PayloadAction<number>) => ({
      ...state,
      pagination: { ...state.pagination, totalCount: action.payload },
    }),
    setHasMore: (state, action: PayloadAction<boolean>) => ({
      ...state,
      pagination: { ...state.pagination, hasMore: action.payload },
    }),
    setIsLoadingMore: (state, action: PayloadAction<boolean>) => ({
      ...state,
      pagination: { ...state.pagination, isLoadingMore: action.payload },
    }),
    setPaginationState: (state, action: PayloadAction<Partial<PaginationState>>) => ({
      ...state,
      pagination: { ...state.pagination, ...action.payload },
    }),
    incrementPage: (state) => ({
      ...state,
      pagination: { ...state.pagination, currentPage: state.pagination.currentPage + 1 },
    }),
    resetPagination: (state) => ({
      ...state,
      pagination: {
        currentPage: 0,
        pageSize: 20,
        totalCount: 0,
        hasMore: true,
        isLoadingMore: false,
      },
      rawApplicationPages: [], // Clear raw application pages on pagination reset
    }),
    setLoadingMore: (state, action: PayloadAction<boolean>) => ({
      ...state,
      pagination: { ...state.pagination, isLoadingMore: action.payload },
    }),

    // Raw application pages actions
    setRawApplicationPages: (state, action: PayloadAction<WaitlistApplicationsApiResponse[]>) => ({
      ...state,
      rawApplicationPages: action.payload,
    }),
    addRawApplicationPage: (state, action: PayloadAction<WaitlistApplicationsApiResponse>) => ({
      ...state,
      rawApplicationPages: [...state.rawApplicationPages, action.payload],
    }),
    clearRawApplicationPages: (state) => ({
      ...state,
      rawApplicationPages: [],
    }),

    // Reset action to clear all cached data and force refresh
    resetAllData: (state) => ({
      ...state,
      leads: [], // Clear existing leads
      filteredLeads: [], // Clear filtered leads  
      rawApplicationPages: [], // Clear cached API data
      lastResetTimestamp: Date.now(), // Track reset for targeted refetch
      pagination: {
        currentPage: 0,
        pageSize: 20,
        totalCount: 0,
        hasMore: true,
        isLoadingMore: false,
      },
      bulkSelection: {
        selectedLeadIds: [],
        isAllSelected: false,
        isIndeterminate: false,
      },
    }),
  },
});

// Helper functions
function filterLeads(leads: WaitlistLead[], searchTerm: string): WaitlistLead[] {
  if (!searchTerm.trim()) {
    return leads;
  }

  const searchLower = searchTerm.toLowerCase();
  return leads.filter(lead =>
    lead.name.toLowerCase().includes(searchLower) ||
    lead.email.toLowerCase().includes(searchLower) ||
    lead.tags.some(tag => {
      if (typeof tag === 'string') {
        return tag.toLowerCase().includes(searchLower);
      }
      return tag.name.toLowerCase().includes(searchLower);
    }) ||
    (lead.floorPlan && lead.floorPlan.toLowerCase().includes(searchLower)) ||
    (lead.notes && lead.notes.toLowerCase().includes(searchLower))
  );
}

function generateFilterTags(
  filters: FilterData,
  occupancyTypes: Array<{ label: string; value: number }>,
  properties: PropertyData[]
): string[] {
  const tags: string[] = [];

  if (filters.property) {
    const propertyData = properties.find(prop => prop.id === filters.property);
    tags.push(propertyData?.name ?? filters.propertyName ?? filters.property);
  }

  if (filters.occupancyType) {
    const occupancyTypeData = occupancyTypes.find(type => type.value === filters.occupancyType);
    tags.push(occupancyTypeData?.label ?? filters.occupancyTypeName ?? filters.occupancyType.toString());
  }

  if (filters.leaseTerm) {
    // Use the lease term ID directly - the actual label will be resolved by the UI component
    // This avoids hardcoding lease terms since they are property-specific and loaded dynamically
    tags.push(filters.leaseTerm);
  }

  if (filters.floorplanPreference.length > 0) {
    tags.push(...filters.floorplanPreference);
  }

  if (filters.spaceOptions.length > 0) {
    tags.push(...filters.spaceOptions);
  }

  if (filters.applicationStageStatusNames && filters.applicationStageStatusNames.length > 0) {
    tags.push(...filters.applicationStageStatusNames.map(name => `Status: ${name}`));
  }

  if (filters.from_date && filters.to_date) {
    tags.push(`Date: ${filters.from_date} to ${filters.to_date}`);
  } else if (filters.from_date) {
    tags.push(`From: ${filters.from_date}`);
  } else if (filters.to_date) {
    tags.push(`To: ${filters.to_date}`);
  }

  if (
    filters.affordableProgramType &&
    filters.affordableProgramType !== 'all' &&
    filters.affordableProgramType !== String(AFFORDABLE_DEFAULT_SUBSIDY_TYPE_ID)
  ) {
    const label = filters.affordableProgramTypeName || filters.affordableProgramType;
    tags.push(`Program: ${label}`);
  }

  return tags;
}

function updateFilterDataFromRemovedTag(
  filterData: FilterData,
  removedTag: string,
  occupancyTypes: Array<{ label: string; value: number }>,
  properties: PropertyData[]
): FilterData {
  const newData = { ...filterData };

  // Check if it's a property filter (by name) - DON'T ALLOW REMOVAL OF PROPERTY
  const propertyData = properties.find(prop => prop.name === removedTag);
  if (propertyData && newData.property === propertyData.id) {
    // Property is required - don't clear it
    // newData.property = '';
  }

  // Check if it's an occupancy type filter
  const occupancyTypeData = occupancyTypes.find(type => type.label === removedTag);
  if (occupancyTypeData && newData.occupancyType === occupancyTypeData.value) {
    newData.occupancyType = 0;
  }

  // Check if it's a lease term filter - DON'T ALLOW REMOVAL OF LEASE TERM
  // Since lease terms are now stored as IDs, we check if the removed tag matches the current lease term
  if (removedTag === filterData.leaseTerm) {
    // Lease term is required - don't clear it
    // newData.leaseTerm = '';
  }

  // Check if it's a floorplan preference filter
  if (newData.floorplanPreference.includes(removedTag)) {
    const removedIndex = newData.floorplanPreference.indexOf(removedTag);
    newData.floorplanPreference = newData.floorplanPreference.filter(fp => fp !== removedTag);

    // Also update the new floorplan fields if they exist
    if (newData.floorplanNames && removedIndex >= 0 && removedIndex < newData.floorplanNames.length) {
      newData.floorplanNames = newData.floorplanNames.filter((_, index) => index !== removedIndex);
    }
    if (newData.floorplanIds && removedIndex >= 0 && removedIndex < newData.floorplanIds.length) {
      newData.floorplanIds = newData.floorplanIds.filter((_, index) => index !== removedIndex);
    }
  }

  // Check if it's a space option filter
  if (newData.spaceOptions.includes(removedTag)) {
    const removedIndex = newData.spaceOptions.indexOf(removedTag);
    newData.spaceOptions = newData.spaceOptions.filter(so => so !== removedTag);

    // Also update the new space option fields if they exist
    if (newData.spaceOptionNames && removedIndex >= 0 && removedIndex < newData.spaceOptionNames.length) {
      newData.spaceOptionNames = newData.spaceOptionNames.filter((_, index) => index !== removedIndex);
    }
    if (newData.spaceOptionIds && removedIndex >= 0 && removedIndex < newData.spaceOptionIds.length) {
      newData.spaceOptionIds = newData.spaceOptionIds.filter((_, index) => index !== removedIndex);
    }
  }

  // Check if it's a status filter tag (format: "Status: <name>")
  if (removedTag.startsWith('Status: ')) {
    const statusName = removedTag.replace('Status: ', '');
    if (newData.applicationStageStatusNames) {
      const removedIndex = newData.applicationStageStatusNames.indexOf(statusName);
      newData.applicationStageStatusNames = newData.applicationStageStatusNames.filter(name => name !== statusName);

      if (newData.applicationStageStatusIds && removedIndex >= 0 && removedIndex < newData.applicationStageStatusIds.length) {
        newData.applicationStageStatusIds = newData.applicationStageStatusIds.filter((_, index) => index !== removedIndex);
      }

      // Rebuild the comma-separated ID string
      if (newData.applicationStageStatusIds && newData.applicationStageStatusIds.length > 0) {
        newData.application_stage_status = newData.applicationStageStatusIds.join(',');
      } else {
        newData.application_stage_status = undefined;
        newData.applicationStageStatusIds = [];
        newData.applicationStageStatusNames = [];
      }
    }
  }

  // Check if it's a date range filter tag
  if (removedTag.startsWith('Date: ') || removedTag.startsWith('From: ') || removedTag.startsWith('To: ')) {
    newData.from_date = undefined;
    newData.to_date = undefined;
  }

  // Check if it's an affordable program type filter tag
  if (removedTag.startsWith('Program: ')) {
    newData.affordableProgramType = undefined;
    newData.subsidyId = undefined;
  }

  return newData;
}

export const {
  // Lead management
  setLeads,
  addLeads,
  addLead,
  updateLead,
  removeLead,

  // Filter management
  setFilters,
  setFilterData,
  setLeaseTermData,
  setActiveFilters,
  removeActiveFilter,
  setFloorplanSelection,
  setSpaceOptionsSelection,
  clearSpaceOptionsSelection,

  // Search management
  setSearchTerm,

  // UI management
  setActiveTab,
  setFilterPopupOpen,
  toggleColumn,
  setColumnVisibility,
  setDashboardVisible,
  toggleDashboardVisible,
  setChartSortBy,

  // Chart management
  setChartData,

  // Stats and misc
  setStats,
  setIsLoading,
  setSelectedLead,

  // Properties and occupancy types
  setProperties,
  setOccupancyTypes,

  // Bulk selection
  toggleSelectLead,
  selectAllLeads,
  deselectAllLeads,
  clearBulkSelection,

  // Tag management
  setTags,
  createTag,
  updateTag,
  deleteTag,
  toggleTagStatus,
  setTagManagementProperty,
  setTagManagementOccupancy,
  clearModifiedTags,
  addModifiedTag,
  reorderTagWithinCategory,

  // Pagination
  setCurrentPage,
  setPageSize,
  setTotalCount,
  setHasMore,
  setIsLoadingMore,
  setPaginationState,
  incrementPage,
  resetPagination,
  setLoadingMore,

  // Raw application pages
  setRawApplicationPages,
  addRawApplicationPage,
  clearRawApplicationPages,

  // Reset action
  resetAllData,
} = waitlistSlice.actions;

export default waitlistSlice.reducer;
